import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useTranslation } from '../../../lib/i18n';
import { useMe } from '../../../lib/me';
import { pushModalEscape } from '../../../lib/modal-escape';
import { clampInlineStart, placeBlock, windowViewport, type BlockPlacement } from '../../../lib/popover-placement';
import { useGenericBi } from '../../../lib/use-generic-bi';
import {
  badgeFor,
  emptyReason,
  membershipOf,
  rankSuggestions,
  UNRESOLVED,
  type PickedRecipient,
  type RecipientCandidate,
  type SuggestionRow,
} from '../../../lib/recipient-field';
import { useContactSearch } from '../../../lib/use-contact-search';
import { useShareMembership } from '../../../lib/use-share-membership';
import { SymphonyMark } from '../../SymphonyMark';
import { Avatar, Card, IconButton, Input, Typography } from '../ui';
import styles from './RecipientField.module.css';

// The share dialog's people field: type a name, pick a person, share with several at once.
//
// Every rule it obeys — what a row may claim, who is excluded, what an empty list means — lives in
// `lib/recipient-field.ts` and is tested there without a DOM. This file is the rendering, and holds no
// decision of its own. That split is the same one `share-flow.ts` makes, and it exists so the two
// phones can implement the identical field from the identical rules.
//
// ## Chips sit ABOVE the field, not inside it
//
// A token-input (chips inline with the caret) would need a hand-rolled `<input>`, which the DS forbids
// — `test/input-spec.test.ts` fails any raw text input that is not registered, precisely so fields
// cannot drift from the 42px DS box. The composer already puts picked contacts in a row above its
// input (`ContactChipRow`), so this is the established shape here rather than a compromise.
//
// ## The suggestions OVERLAY the dialog — they do not grow it
//
// A list in the flow pushed everything under it down: the sheet grew taller with every keystroke and
// "Who has access" and the Share button moved while the reader was still typing. Every combobox they
// have used — Gmail, Drive — floats over the page instead, and so does this one.
//
// It portals to `<body>` at `position: fixed`, because both share dialogs render inside a `scrollBody`
// Modal: an absolutely-positioned list is clipped by that scroller the moment it is longer than the
// remaining body height.
//
// The reason an earlier pass argued against portalling was that `OverflowMenu` CLOSES on any scroll,
// and a list that vanishes mid-type is worse than one that reflows. That is a property of
// `OverflowMenu`, not of portals — it closes because it cannot re-place itself. This one RE-PLACES on
// scroll and resize (capture phase, so the modal's own scroller is caught), reusing the same
// `lib/popover-placement.ts` every other popover here uses, so it stays glued to the field and inside
// the window, and flips above the field when there is no room below.
//
// It carries no scroller of its own. `rankSuggestions` already caps the list at six rows, so there is
// nothing to scroll to — and a scroller inside a scrolling modal gives the reader two wheels over one
// piece of content, where the outer one stops working as soon as the pointer is over the inner one.
//
// ## Written-down divergence: the phones keep the list IN the flow
//
// This overlay is web-only, deliberately. Both share sheets on a phone are bottom sheets sized to
// their content, and a list that grows the sheet is what every native picker does; a popover floating
// over a bottom sheet is not an idiom either platform uses. The complaint this fixes — the Share
// button moving while you type — is a desktop-dialog problem, where the button sits at a fixed place
// in a fixed-height card. One feature, two idioms, not a gap. If Design wants the phones to overlay
// too, that moves both of them together.
//
// ## Nothing is offered until the field is asked
//
// The list appears on the first keystroke. There is no default list, because the only thing we could
// put in one is `/api/contacts` with no search — `queryContacts({ paging: { limit: 20 } })`, no sort —
// and the first twenty rows a CRM happens to return is not a reason to put five particular people in
// front of somebody about to share a document with them.
//
// ## The Symphony mark, and the silence behind it
//
// A row says "on Symphony" when `ResolveShareRecipients` said so, "your contact" when they are from
// your own People, and NOTHING when the resolve did not answer. That third case is not a fallback —
// it is why the wire field is a `BoolValue` and why the service refuses to guess: the predicates
// behind membership fail toward TRUE by design, which is right for a gate and a lie in a badge, where
// a stranger painted "on Symphony" is the assurance somebody acts on before handing over a document.
// `useShareMembership` returns a MAP, and a missing key IS that third state — never `false`.
//
// The badge is a convenience, not a control. The server-side stranger gate still runs at Share time
// whatever this said, so a resolve that is refused, rate-limited or slow costs a mark and nothing else.

export interface RecipientFieldProps {
  /** People already holding a grant, so the field cannot offer a no-op. */
  alreadyHasAccess: string[];
  picked: PickedRecipient[];
  onPickedChange: (picked: PickedRecipient[]) => void;
  /** Shown against the field — a rejected address, a failed share. */
  errorMessage?: string;
  disabled?: boolean;
  /** Enter with nothing highlighted and nothing typed. Lets the dialog submit from the field. */
  onSubmit?: () => void;
  /** The 572 modal-action vocabulary this field's clicks report under. */
  bi: { pageName: string; modalName: string };
}

const DEBOUNCE_MS = 220;

export function RecipientField({ alreadyHasAccess, picked, onPickedChange, errorMessage, disabled, onSubmit, bi }: RecipientFieldProps) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const me = useMe();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  // `null` means NOTHING is highlighted, and it is the resting value. A default of 0 pre-selects the
  // first recent contact, so Enter — which the owner presses to send — silently adds a person they
  // never looked at instead. Only an arrow key or the pointer puts a row under the cursor.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [pos, setPos] = useState<(BlockPlacement & { left: number; width: number }) | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // NOT asked until something is typed. The empty-query branch of `/api/contacts` is
  // `queryContacts({ paging: { limit: 20 } })` with NO sort — it is not "recent contacts" whatever
  // the route comment says, just the first twenty rows the CRM happens to return. Showing that as a
  // suggestion list implies a reason those five people are on screen, and there is none.
  const search = useContactSearch(debounced, debounced.length > 0);
  const contacts: RecipientCandidate[] = useMemo(
    () => (search.status === 'ready' || search.status === 'loading' ? search.contacts : []),
    [search],
  );

  const input = useMemo(
    () => ({
      query,
      contacts,
      contactsStatus: search.status === 'error' ? ('error' as const) : search.status === 'loading' ? ('loading' as const) : ('ready' as const),
      alreadyHasAccess,
      pending: picked.map((p) => p.identifier),
      // Sharing with yourself is not a share — the server would grant the owner access to their own
      // file. The rules module has always excluded `self`; it is passed here because this is the first
      // caller that knows who the owner is.
      self: me?.email ? [me.email] : [],
    }),
    [query, contacts, search.status, alreadyHasAccess, picked, me?.email],
  );

  const rows = useMemo(() => rankSuggestions(input), [input]);

  // The rows on screen plus the people already picked. `rankSuggestions` caps rows at six, so one
  // settled query is one request — inside the service's per-caller limit, and a burst of typing that
  // does trip it degrades to no mark rather than to a wrong one.
  const resolveTargets = useMemo(
    () => [...rows.map((r) => r.identifier), ...picked.map((p) => p.identifier)],
    [rows, picked],
  );
  const membership = useShareMembership(resolveTargets);
  // A run in flight must not be able to grow the list it is working through: anyone added mid-run is
  // dropped when the run's result is applied, so the row would silently do nothing. And nothing opens
  // before the field has been asked a question.
  const listOpen = query.trim().length > 0 && rows.length > 0 && !dismissed && !disabled;
  const empty = emptyReason(input);
  const active = activeIndex !== null ? rows[activeIndex] : undefined;

  // The highlight must not survive the list it pointed into. `rows` recomputes on every keystroke (the
  // name filter is local; only the fetch is debounced), so keying this on the debounced value left the
  // cursor pointing past a list that had already shrunk — for 220ms nothing was highlighted and Enter
  // did nothing at all.
  useEffect(() => {
    setActiveIndex(null);
    setDismissed(false);
  }, [query, picked.length]);

  // Re-placed rather than closed. The gap and the window edge are the same 4/8 every other popover
  // here uses; `placeBlock` flips above the field when the list does not fit below it.
  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const viewport = windowViewport();
    const block = placeBlock(rect, listRef.current?.offsetHeight, viewport, 4, 8);
    setPos({ ...block, left: clampInlineStart(rect.left, rect.width, viewport, 8), width: rect.width });
  }, []);

  // Before paint, so the list never renders one frame at a stale position.
  useLayoutEffect(() => {
    if (!listOpen) {
      setPos(null);
      return undefined;
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
    // `rows.length` is in the deps because the list's own height decides which side it opens on.
  }, [listOpen, place, rows.length]);

  // Escape closes the SUGGESTIONS, not the dialog. Modal claims Escape on `window` in the capture
  // phase, so a React handler here can never see it first — but the claim is a stack, and this pushes
  // the field on top of it while the list is up. Without this, the standard combobox reflex throws
  // away the modal and every chip in it.
  useEffect(() => {
    if (!listOpen) return undefined;
    return pushModalEscape(() => setDismissed(true));
  }, [listOpen]);

  const pick = (row: SuggestionRow) => {
    const person: PickedRecipient =
      row.kind === 'contact'
        ? { id: row.candidate.id, name: row.candidate.name, identifier: row.identifier, membership: UNRESOLVED, isContact: true }
        : { name: row.identifier, identifier: row.identifier, membership: UNRESOLVED, isContact: false };
    logModalActionBi({
      page_name: bi.pageName,
      modal_name: bi.modalName,
      action: row.kind === 'contact' ? 'pick_contact' : 'pick_typed_address',
    });
    onPickedChange([...picked, person]);
    setQuery('');
    setDebounced('');
    inputRef.current?.focus();
  };

  const remove = (identifier: string) => {
    logModalActionBi({ page_name: bi.pageName, modal_name: bi.modalName, action: 'remove_recipient' });
    onPickedChange(picked.filter((p) => p.identifier !== identifier));
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && listOpen) {
      e.preventDefault();
      setActiveIndex((i) => (i === null ? 0 : (i + 1) % rows.length));
    } else if (e.key === 'ArrowUp' && listOpen) {
      e.preventDefault();
      setActiveIndex((i) => (i === null ? rows.length - 1 : (i - 1 + rows.length) % rows.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Three things Enter can mean, in the order the owner means them: take the row I am pointing at,
      // take the address I just finished typing, or send to the people already on screen.
      const typed = listOpen ? rows.find((r) => r.kind === 'literal') : undefined;
      if (active) pick(active);
      else if (typed) pick(typed);
      else if (!query.trim() && picked.length) onSubmit?.();
    } else if (e.key === 'Backspace' && !query && picked.length && !disabled) {
      // Backspace on an empty field takes the last chip — the token-input convention, and the only
      // way to undo a pick without reaching for the mouse.
      remove(picked[picked.length - 1].identifier);
    }
  };

  return (
    <div className={styles.field}>
      {picked.length > 0 && (
        <ul className={styles.chips} aria-label={t('share.recipients.picked')}>
          {picked.map((person) => (
            <li key={person.identifier} className={styles.chip}>
              <Avatar initials={avatarInitials(person.name)} size={20} color={avatarSlotFor(person.identifier)} />
              <Typography variant="p3" color="default" component="span" className={styles.chipName}>
                {person.name}
              </Typography>
              <IconButton
                size="tiny"
                variant="text"
                aria-label={t('share.recipients.remove', { name: person.name })}
                disabled={disabled}
                onClick={() => remove(person.identifier)}
              >
                <X size={14} aria-hidden />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      <div ref={anchorRef} className={styles.anchor}>
        <Input
            ref={inputRef}
          label={t('share.recipients.label')}
          placeholder={t('share.recipients.placeholder')}
          value={query}
          disabled={disabled}
          status={errorMessage ? 'error' : 'default'}
          statusMessage={errorMessage}
          role="combobox"
          // The browser's own autofill panel would draw straight over the suggestions, offering a second
          // list of addresses that this field knows nothing about.
          autoComplete="off"
          aria-expanded={listOpen}
          aria-controls={listOpen ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={active ? `${listboxId}-${activeIndex}` : undefined}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
      </div>

      {listOpen &&
        pos &&
        createPortal(
          <Card
            // SUNKEN, not outline. The outline skin's fill is literally `transparent` — correct for a
            // card sitting ON a surface, and see-through once the card FLOATS: the first cut let "Who
            // has access" and the Share button read straight through the suggestions. Sunken is the
            // opaque one, and it is the skin that flips to #333232 in dark mode.
            variant="sunken"
            size="sm"
            disablePadding
            className={styles.listCard}
            style={{ top: pos.top ?? 'auto', bottom: pos.bottom ?? 'auto', left: pos.left, width: pos.width }}
          >
            <ul ref={listRef} className={styles.list} id={listboxId} role="listbox" aria-label={t('share.recipients.label')}>
            {rows.map((row, i) => (
              <li
                key={row.kind === 'contact' ? row.candidate.id : `literal-${row.identifier}`}
                id={`${listboxId}-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`${styles.row} ${i === activeIndex ? styles.rowActive : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
                onClick={() => pick(row)}
              >
                <SuggestionContent row={row} membership={membership} />
              </li>
              ))}
            </ul>
          </Card>,
          document.body,
        )}

      {/* Four reasons, not one empty list — a read that is still out or that FAILED is not an empty
          address book, which is the distinction `share-flow.ts:accessLine` exists to keep. It is said
          only when the field was asked something and has nothing to answer with: printing "looking
          through your contacts" under a row it is already offering contradicts the row, and printing
          anything at all under an untouched field reads as a problem with it. */}
      {empty && rows.length === 0 && (
        <Typography variant="p3" color="muted" component="p" className={styles.empty}>
          {t(EMPTY_KEY[empty])}
        </Typography>
      )}
    </div>
  );
}

const EMPTY_KEY = {
  loading: 'share.recipients.loading',
  readFailed: 'share.recipients.readFailed',
  typeMore: 'share.recipients.typeMore',
  alreadyAdded: 'share.recipients.alreadyAdded',
} as const;

function SuggestionContent({ row, membership }: { row: SuggestionRow; membership: Map<string, boolean> }) {
  const { t } = useTranslation();
  // `Map.get` answers `undefined` for a key nobody answered about, and `membershipOf` turns exactly
  // that into `unresolved` — so this reads as member / not member / we did not find out, with no
  // chance of a `?? false` collapsing the last two.
  const asPerson = (name: string, isContact: boolean): PickedRecipient => ({
    name,
    identifier: row.identifier,
    isContact,
    membership: membershipOf(membership.get(row.identifier)),
  });

  if (row.kind === 'literal') {
    return (
      <>
        <Avatar initials="+" size={24} color={avatarSlotFor(row.identifier)} />
        <span className={styles.rowText}>
          <Typography variant="p2" color="default" component="span" className={styles.rowName}>
            {t('share.recipients.useTyped', { value: row.identifier })}
          </Typography>
          <Typography variant="p3" color="muted" component="span" className={styles.rowDetail}>
            {t('share.recipients.notInContacts')}
          </Typography>
        </span>
        {/* A typed address can be a colleague too — the mark is about the ACCOUNT, not about whether
            they happen to be in your address book. */}
        {badgeFor(asPerson(row.identifier, false)) === 'symphony' && <MemberMark />}
      </>
    );
  }
  const badge = badgeFor({ ...asPerson(row.candidate.name, true), id: row.candidate.id });
  return (
    <>
      <Avatar initials={avatarInitials(row.candidate.name)} size={24} color={avatarSlotFor(row.identifier)} />
      <span className={styles.rowText}>
        <Typography variant="p2" color="default" component="span" className={styles.rowName}>
          {row.candidate.name}
        </Typography>
        <Typography variant="p3" color="muted" component="span" className={styles.rowDetail}>
          {row.identifier}
        </Typography>
      </span>
      {/* One mark per row, and `badgeFor` picks which: on Symphony outranks in-your-contacts, because
          it is the fact that changes what sharing DOES for that person. */}
      {badge === 'symphony' && <MemberMark />}
      {badge === 'contact' && (
        <Typography variant="p3" color="muted" component="span" className={styles.rowBadge}>
          {t('share.recipients.yourContact')}
        </Typography>
      )}
    </>
  );
}


/**
 * "Already on Symphony" — the product mark beside the words, so it reads as identity rather than as
 * one more grey label.
 *
 * The word is there for anybody who cannot see the glyph; the glyph is `aria-hidden` and the row's
 * accessible name comes from the text.
 */
function MemberMark() {
  const { t } = useTranslation();
  return (
    <span className={styles.memberMark}>
      <SymphonyMark size={14} />
      <Typography variant="p3" color="default" component="span">
        {t('share.recipients.onSymphony')}
      </Typography>
    </span>
  );
}
